import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { combineDateAndTime, extractDate, extractTime } from 'utils/date-time'

/** Delivery stops use separate start/end dates (per load confirmation mockup). */
export function useDeliveryDateTimeSync(eventIndex: number) {
  const { watch, setValue, getValues } = useFormContext()
  const isInitializing = useRef(true)
  const skipStartDateUpdate = useRef(false)
  const skipEndDateUpdate = useRef(false)
  const skipStartTimeUpdate = useRef(false)
  const skipEndTimeUpdate = useRef(false)

  const basePath = `deliveries.${eventIndex}`
  const startTime = watch(`${basePath}.startTime`)
  const endTime = watch(`${basePath}.endTime`)

  const startTimeOnlyField = `_lc_startTimeOnly_deliveries_${eventIndex}`
  const endTimeOnlyField = `_lc_endTimeOnly_deliveries_${eventIndex}`

  const startTimeOnly = watch(startTimeOnlyField)
  const endTimeOnly = watch(endTimeOnlyField)

  const lastSyncedStartTime = useRef<string | null>(null)
  const lastSyncedEndTime = useRef<string | null>(null)

  useEffect(() => {
    lastSyncedStartTime.current = null
    lastSyncedEndTime.current = null
    isInitializing.current = true
  }, [eventIndex])

  useEffect(() => {
    if (startTime !== lastSyncedStartTime.current) {
      lastSyncedStartTime.current = startTime ?? null
      setValue(
        startTimeOnlyField,
        startTime ? extractTime(startTime) : '',
        { shouldValidate: false }
      )
    }
    if (endTime !== lastSyncedEndTime.current) {
      lastSyncedEndTime.current = endTime ?? null
      setValue(
        endTimeOnlyField,
        endTime ? extractTime(endTime) : '',
        { shouldValidate: false }
      )
    }
    if (isInitializing.current) {
      isInitializing.current = false
    }
  }, [startTime, endTime, setValue, startTimeOnlyField, endTimeOnlyField])

  useEffect(() => {
    if (!isInitializing.current && !skipStartDateUpdate.current && startTime) {
      const currentTimeOnly = getValues(startTimeOnlyField)
      if (currentTimeOnly) {
        const dateStr = extractDate(startTime)
        if (dateStr) {
          const combined = combineDateAndTime(dateStr, currentTimeOnly)
          if (combined) {
            skipStartDateUpdate.current = true
            setValue(`${basePath}.startTime`, combined, { shouldValidate: true })
            setTimeout(() => {
              skipStartDateUpdate.current = false
            }, 0)
          }
        }
      }
    }
  }, [startTime, setValue, basePath, getValues, startTimeOnlyField])

  useEffect(() => {
    if (!isInitializing.current && !skipEndDateUpdate.current && endTime) {
      const currentTimeOnly = getValues(endTimeOnlyField)
      if (currentTimeOnly) {
        const dateStr = extractDate(endTime)
        if (dateStr) {
          const combined = combineDateAndTime(dateStr, currentTimeOnly)
          if (combined) {
            skipEndDateUpdate.current = true
            setValue(`${basePath}.endTime`, combined, { shouldValidate: true })
            setTimeout(() => {
              skipEndDateUpdate.current = false
            }, 0)
          }
        }
      }
    }
  }, [endTime, setValue, basePath, getValues, endTimeOnlyField])

  useEffect(() => {
    if (!isInitializing.current && !skipStartTimeUpdate.current && startTimeOnly) {
      const currentStartTime = getValues(`${basePath}.startTime`)
      const dateStr = currentStartTime ? extractDate(currentStartTime) : null
      if (dateStr) {
        const combined = combineDateAndTime(dateStr, startTimeOnly)
        if (combined) {
          skipStartTimeUpdate.current = true
          setValue(`${basePath}.startTime`, combined, { shouldValidate: true })
          setTimeout(() => {
            skipStartTimeUpdate.current = false
          }, 0)
        }
      }
    }
  }, [startTimeOnly, setValue, basePath, getValues])

  useEffect(() => {
    if (!isInitializing.current && !skipEndTimeUpdate.current && endTimeOnly) {
      const currentEndTime = getValues(`${basePath}.endTime`)
      const dateStr = currentEndTime ? extractDate(currentEndTime) : extractDate(startTime)
      if (dateStr) {
        const combined = combineDateAndTime(dateStr, endTimeOnly)
        if (combined) {
          skipEndTimeUpdate.current = true
          setValue(`${basePath}.endTime`, combined, { shouldValidate: true })
          setTimeout(() => {
            skipEndTimeUpdate.current = false
          }, 0)
        }
      }
    }
  }, [endTimeOnly, setValue, basePath, getValues, startTime])

  useEffect(() => {
    if (!isInitializing.current && startTime && !endTime) {
      setValue(`${basePath}.endTime`, startTime, { shouldValidate: true })
    }
  }, [startTime, endTime, setValue, basePath])

  return { startTimeOnlyField, endTimeOnlyField, basePath }
}
